import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { announcementApi } from '../services/softwareFactoryApi';
import type {
  ListAnnouncementsParams,
  SfUserAnnouncementCreate,
  SfUserAnnouncementTranslationInput,
  SfUserAnnouncementUpdate,
} from '../types';

export const announcementKeys = {
  all: ['sfAnnouncements'] as const,
  list: (params?: ListAnnouncementsParams) =>
    [...announcementKeys.all, 'list', params] as const,
  detail: (id: string) => [...announcementKeys.all, 'detail', id] as const,
  mine: (language: string) => [...announcementKeys.all, 'mine', language] as const,
};

export function useAnnouncements(params?: ListAnnouncementsParams) {
  return useQuery({
    queryKey: announcementKeys.list(params),
    queryFn: () => announcementApi.list(params),
  });
}

export function useAnnouncement(id: string) {
  return useQuery({
    queryKey: announcementKeys.detail(id),
    queryFn: () => announcementApi.get(id),
    enabled: !!id,
  });
}

export function useCreateAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SfUserAnnouncementCreate) => announcementApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.all });
    },
  });
}

export function useUpdateAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SfUserAnnouncementUpdate }) =>
      announcementApi.update(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.all });
      queryClient.invalidateQueries({ queryKey: announcementKeys.detail(variables.id) });
    },
  });
}

export function useDeleteAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => announcementApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.all });
    },
  });
}

/** Status transition: publish / unpublish / archive. */
export function useAnnouncementStatusTransition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'publish' | 'unpublish' | 'archive' }) =>
      announcementApi[action](id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.all });
      queryClient.invalidateQueries({ queryKey: announcementKeys.detail(variables.id) });
    },
  });
}

export function useUpsertAnnouncementTranslation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      language,
      data,
    }: {
      id: string;
      language: string;
      data: SfUserAnnouncementTranslationInput;
    }) => announcementApi.upsertTranslation(id, language, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.detail(variables.id) });
    },
  });
}

export function useDeleteAnnouncementTranslation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, language }: { id: string; language: string }) =>
      announcementApi.deleteTranslation(id, language),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.detail(variables.id) });
    },
  });
}

export function useSetAnnouncementWorkItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, workItemIds }: { id: string; workItemIds: string[] }) =>
      announcementApi.setWorkItems(id, workItemIds),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.detail(variables.id) });
    },
  });
}

export function useSetAnnouncementInitiatives() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, initiativeIds }: { id: string; initiativeIds: string[] }) =>
      announcementApi.setInitiatives(id, initiativeIds),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.detail(variables.id) });
    },
  });
}

/**
 * Delivery hook — published, non-dismissed announcements for the current user.
 * Fetched once per session (long stale time); consumed by the login modal + bell.
 *
 * `meta.persist: false` keeps this out of the IndexedDB query cache: the `seen`
 * flag and dismissal state are per-user and change server-side, so a stale
 * restored copy would re-trigger the login modal on every reload. Each app
 * load must re-fetch it fresh.
 */
export function useMyAnnouncements(language: string, enabled = true) {
  return useQuery({
    queryKey: announcementKeys.mine(language),
    queryFn: () => announcementApi.listMine(language),
    enabled,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    meta: { persist: false },
  });
}

/**
 * Records a view/dismissal. Intentionally does NOT invalidate the `me` query —
 * the delivery list stays stable for the session so the modal can iterate it
 * without items shifting underneath; seen/dismissed state is tracked locally.
 */
export function useRecordAnnouncementView() {
  return useMutation({
    mutationFn: ({ id, dismissed }: { id: string; dismissed: boolean }) =>
      announcementApi.recordView(id, dismissed),
  });
}
